import { Avatar, BasicButton, Loading, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import { useBoolean } from "@/hooks/useBoolean";
import useProfile from "@/hooks/useProfile";
import useTeamMutation from "@/hooks/useTeamMutation";
import { getTeamQueryOptions } from "@/queries";
import { usePrompt } from "@/stores/prompt";
import AddIcon from "@mui/icons-material/Add";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SearchIcon from "@mui/icons-material/Search";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import DrawerMoreAction from "./-components/DrawerMoreAction";
import DrawerViewMember from "./-components/DrawerViewMember";

export const Route = createFileRoute("/dashboard/teams/$id/")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const teamId = Route.useParams({
		select: (state) => state.id,
	});

	const { profile } = useProfile();

	const [memberId, setMemberId] = useState<number | null>(null);

	const {
		value: isOpenMoreMenu,
		setTrue: openMoreMenu,
		setFalse: closeMoreMenu,
	} = useBoolean();

	const {
		data: { team },
	} = useSuspenseQuery(getTeamQueryOptions(teamId));

	const { leave } = useTeamMutation();
	const openPrompt = usePrompt((state) => state.open);

	const profileRole = useMemo<"owner" | "member">(() => {
		if (profile?.id !== team.creator?.id) return "member";
		return "owner";
	}, [profile, team]);

	const onContextMenuMember = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, memberId: number) => {
			if (memberId === profile?.id) return;
			e.stopPropagation();
			e.preventDefault();
			return setMemberId(memberId);
		},
		[profile],
	);

	const handleOnLeave = useCallback(async () => {
		try {
			await leave.mutateAsync(teamId);
			return navigate({
				replace: true,
				to: "/dashboard/contacts/contact/teams",
			});
		} catch (error) {
			return toast.error("Failed to leave team");
		}
	}, [leave, navigate, teamId]);

	const onClickLeave = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e?.preventDefault();
			return openPrompt({
				title: "Leave Team?",
				description: `Are you sure you wish to leave ${team.name}?`,
				positiveButton: {
					label: "Leave",
					variant: "contained",
					color: "error",
					size: "large",
					onClick: handleOnLeave,
				},
				negativeButton: {
					label: "Cancel",
					variant: "outlined",
					color: "primary",
					size: "large",
				},
			});
		},
		[openPrompt, handleOnLeave, team.name],
	);

	return (
		<>
			<BackAppBar
				title={team.name}
				className="!bg-gradient-account !text-white [&_svg]:!text-white"
				titleClassName="flex-1"
				rightContent={
					profileRole === "owner" ? (
						<IconButton onClick={openMoreMenu}>
							<MoreVertRoundedIcon className="!text-white" />
						</IconButton>
					) : null
				}
			/>
			<div className="h-full flex flex-col bg-gray-light gap-2">
				<div className="relative bg-white">
					<div className="container py-4 space-y-3">
						<div className="flex flex-row justify-between items-center">
							<Typography fontWeight={700} fontSize={14}>
								Team Members ({team.users.length})
							</Typography>
							<Link
								to="/dashboard/teams/$id/members/search"
								params={{ id: team.id }}
								search={{ type: "users" }}
							>
								<IconButton>
									<SearchIcon className="text-zinc-950" />
								</IconButton>
							</Link>
						</div>
						<List className="divide-y divide-gray-light">
							{profileRole === "owner" ? (
								<ListItem disablePadding>
									<ListItemButton
										onClick={() =>
											navigate({
												to: "/dashboard/teams/$id/edit/members",
												params: { id: teamId },
											})
										}
									>
										<ListItemAvatar>
											<Avatar className="!bg-primary-light !text-primary">
												<AddIcon />
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											className="text-primary !uppercase"
											primary="Add Team Member"
										/>
									</ListItemButton>
								</ListItem>
							) : null}
							{team.users.map((member) => (
								<ListItem
									key={member.id}
									disablePadding
									secondaryAction={
										member.id === team.creator?.id ? (
											<div className="bg-primary-light px-2 py-1 rounded-md">
												<Typography
													fontWeight={500}
													fontSize={12}
													className="text-primary"
												>
													Owner
												</Typography>
											</div>
										) : null
									}
								>
									<ListItemButton
										onContextMenu={(e) => onContextMenuMember(e, member.id)}
									>
										<ListItemAvatar>
											<Avatar
												alt={member.first_name || "L"}
												className="!bg-gray-light !text-dark"
												src={member.avatar ?? "/broken-image.jpg"}
											/>
										</ListItemAvatar>
										<ListItemText
											primary={[
												member.fullname,
												profile?.id === member.id ? " (Me)" : "",
											].join("")}
										/>
									</ListItemButton>
								</ListItem>
							))}
						</List>
					</div>
				</div>
				{profileRole === "owner" ? (
					<div className="relative bg-white">
						<div className="container py-4 space-y-3">
							<div className="flex flex-row justify-between items-center">
								<Typography fontWeight={700} fontSize={14}>
									Pending Acceptance ({team.pending_invitations.length})
								</Typography>
								<Link
									to="/dashboard/teams/$id/members/search"
									params={{ id: team.id }}
									search={{ type: "pending_invitations" }}
								>
									<IconButton>
										<SearchIcon className="text-zinc-950" />
									</IconButton>
								</Link>
							</div>
							<List className="divide-y divide-gray-light">
								{team.pending_invitations.map((member) => (
									<ListItem
										key={member.id}
										disablePadding
										secondaryAction={
											member.id === team.creator?.id ? (
												<div className="bg-primary-light px-2 py-1 rounded-md">
													<Typography
														fontWeight={500}
														fontSize={12}
														className="text-primary"
													>
														Owner
													</Typography>
												</div>
											) : null
										}
									>
										<ListItemButton
											onContextMenu={(e) => onContextMenuMember(e, member.id)}
										>
											<ListItemAvatar>
												<Avatar
													alt={member.first_name || "L"}
													className="!bg-gray-light !text-dark"
													src={member.avatar ?? "/broken-image.jpg"}
												/>
											</ListItemAvatar>
											<ListItemText
												primary={[
													member.fullname,
													profile?.id === member.id ? " (Me)" : "",
												].join("")}
											/>
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</div>
					</div>
				) : (
					<div className="container py-4 bg-white">
						<BasicButton
							fullWidth
							variant="outlined"
							color="error"
							className="!h-12 text-sm font-semibold !shadow-none"
							onClick={onClickLeave}
							startIcon={<LogoutRoundedIcon />}
						>
							Leave Team
						</BasicButton>
					</div>
				)}
			</div>
			<Drawer
				anchor="bottom"
				open={isOpenMoreMenu}
				onClose={closeMoreMenu}
				classes={{
					paper: "wrapper rounded-t-2xl",
				}}
			>
				<DrawerMoreAction teamId={teamId} onClose={closeMoreMenu} />
			</Drawer>
			<Drawer
				anchor="bottom"
				open={!!memberId}
				onClose={() => setMemberId(null)}
				classes={{
					paper: "wrapper rounded-t-2xl",
				}}
			>
				{memberId ? (
					<DrawerViewMember
						memberId={memberId}
						onClose={() => setMemberId(null)}
					/>
				) : null}
			</Drawer>
			<Loading status={leave.isPending} />
		</>
	);
}
